import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { fetchWithMaintenanceDetection } from '../services/api';
import {
  refreshAccessToken as singleFlightRefresh,
  AuthSessionExpiredError,
} from '../services/tokenRefresh';

// Get base URL from environment or default
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

interface User {
  id: number;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;  // API returns camelCase
  lastName?: string;   // API returns camelCase
  // Server-side flag (CustomUserSerializer + LoginResponseSerializer). Used as
  // a UI gate to avoid pinging superuser-only endpoints like
  // /api/auth/impersonate/list/ from regular reps (#1949).
  is_superuser?: boolean;
  // Server-side manager gate (apps.manager.access.is_manager) surfaced on the
  // auth response (#2475). Shows the supervisor "My Team" tab.
  is_manager?: boolean;
  // #2104 feature gate (django-flags split_time_at_checkout, default OFF).
  // When true, checkout exposes editable per-task time allocation; otherwise
  // checkout shows only the tracked timer time. On the login payload only.
  split_time_at_checkout?: boolean;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// JWT decoder
function decodeJwt(token: string): { exp?: number } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) {
      validateToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Restore the session from the cached identity instead of logging out.
  // Used on TRANSIENT failures (offline cold start, dropped refresh, 5xx):
  // we still hold tokens, so trust the last server-confirmed user (#2114).
  // Only when there is no cached identity at all do we fall back to the
  // legacy clear-everything behavior.
  const restoreFromCache = (accessToken: string | null) => {
    if (accessToken) {
      const cached = localStorage.getItem('authUser');
      if (cached) {
        try {
          setUser(JSON.parse(cached));
          setToken(accessToken);
          return;
        } catch {
          // corrupt cache — fall through
        }
      }
    }
    clearAuth();
  };

  // Validate token and get user
  const validateToken = async (accessToken: string) => {
    try {
      const response = await fetchWithMaintenanceDetection(`${BASE_URL}/api/auth/user/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        // /api/auth/user/ may omit is_manager (it's on the login payload) —
        // preserve the cached flag so the supervisor tab survives reloads (#2475).
        if (userData.is_manager === undefined) {
          try {
            userData.is_manager = !!JSON.parse(localStorage.getItem('authUser') || '{}').is_manager;
          } catch {
            /* corrupt cache — leave undefined */
          }
        }
        // Likewise, split_time_at_checkout (#2104) is on the login payload only —
        // preserve the cached value across /api/auth/user/ refreshes.
        if (userData.split_time_at_checkout === undefined) {
          try {
            userData.split_time_at_checkout = !!JSON.parse(
              localStorage.getItem('authUser') || '{}'
            ).split_time_at_checkout;
          } catch {
            /* corrupt cache — leave undefined */
          }
        }
        setToken(accessToken);
        setUser(userData);
        localStorage.setItem('authUser', JSON.stringify(userData));
      } else if (response.status === 401 || response.status === 403) {
        // Access token rejected — refresh decides whether the session is
        // really over (AuthSessionExpiredError) or just hiccuped.
        await refreshAndLoadUser();
      } else {
        // Transient server trouble (5xx/maintenance) — not an auth verdict.
        restoreFromCache(accessToken);
      }
    } catch {
      // NETWORK error — offline cold start must not log the rep out (#2114).
      restoreFromCache(accessToken);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh the access token (single-flight, shared with apiFetch's 401
  // retry — see tokenRefresh.ts) and re-sync the user object. Failure
  // classes (#2114): only a definitive AUTH failure clears the session;
  // transient failures keep the rep logged in on cached identity.
  const refreshAndLoadUser = async () => {
    try {
      const access = await singleFlightRefresh();
      setToken(access);

      try {
        const userResponse = await fetchWithMaintenanceDetection(`${BASE_URL}/api/auth/user/`, {
          headers: {
            Authorization: `Bearer ${access}`,
            'Content-Type': 'application/json',
          },
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
          localStorage.setItem('authUser', JSON.stringify(userData));
          return;
        }
      } catch {
        // network blip right after a successful refresh — cached user below
      }
      restoreFromCache(access);
    } catch (err) {
      if (err instanceof AuthSessionExpiredError) {
        // Refresh token rejected — the session is genuinely over.
        clearAuth();
      } else {
        // Transient (network/5xx): keep the session; the next apiFetch 401
        // or visibility change will retry the refresh.
        restoreFromCache(localStorage.getItem('accessToken'));
      }
    }
  };

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetchWithMaintenanceDetection(`${BASE_URL}/api/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            errorData.non_field_errors?.[0] ||
            'Invalid email or password'
        );
      }

      const data = await response.json();

      // Store tokens (API returns tokens nested under 'jwt')
      const accessToken = data.jwt?.access || data.access;
      const refreshToken = data.jwt?.refresh || data.refresh;
      // The login endpoint emits is_superuser at the top level of the
      // response (LoginResponseSerializer), not inside the JWT's user payload.
      // Merge it onto userData so downstream gates (e.g. impersonate list)
      // can read user.is_superuser without an extra round-trip (#1949).
      const baseUser = data.jwt?.user || data.user || {};
      const userData = {
        ...baseUser,
        is_superuser: !!data.is_superuser,
        is_manager: !!data.is_manager,
        split_time_at_checkout: !!data.split_time_at_checkout,
      };

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      // Cache the identity so transient failures can restore the session
      // without a server round-trip (#2114).
      localStorage.setItem('authUser', JSON.stringify(userData));

      // Also store associate and permissions for the app
      if (data.associate) {
        localStorage.setItem('associate', JSON.stringify(data.associate));
      }
      if (data.permissions) {
        localStorage.setItem('permissions', JSON.stringify(data.permissions));
      }
      // Store impersonation info if present
      if (data.impersonating) {
        localStorage.setItem('impersonating', JSON.stringify(data.impersonating));
      } else {
        localStorage.removeItem('impersonating');
      }

      // #2025: profile-verification gate signal. The ProfileVerifyGate reads
      // this and routes the rep to /profile/verify when required (online-only).
      if (data.profile_verification?.required) {
        localStorage.setItem('profileVerification', JSON.stringify(data.profile_verification));
      } else {
        localStorage.removeItem('profileVerification');
      }

      setToken(accessToken);
      setUser(userData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    clearAuth();
    // Broadcast logout to other tabs
    localStorage.setItem('logout', Date.now().toString());
  }, []);

  // Clear auth state
  const clearAuth = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('associate');
    localStorage.removeItem('permissions');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('profileVerification');
  };

  // Listen for logout from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'logout') {
        clearAuth();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Stay in sync with the single-flight refresher (tokenRefresh.ts), which
  // also serves apiFetch's 401 retry path (#2115): adopt new access tokens
  // so the expiry timer below reschedules, and clear state when the refresh
  // token is definitively rejected.
  useEffect(() => {
    const onRefreshed = (e: Event) => {
      const access = (e as CustomEvent<{ access?: string }>).detail?.access;
      if (access) setToken(access);
    };
    const onExpired = () => clearAuth();
    window.addEventListener('rdm:token-refreshed', onRefreshed);
    window.addEventListener('rdm:session-expired', onExpired);
    return () => {
      window.removeEventListener('rdm:token-refreshed', onRefreshed);
      window.removeEventListener('rdm:session-expired', onExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #2115: iOS freezes JS timers while a standalone PWA is suspended, so the
  // expiry timer below can fire arbitrarily late. On resume, check expiry
  // immediately instead of trusting the timer.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const access = localStorage.getItem('accessToken');
      if (!access) return;
      const payload = decodeJwt(access);
      if (!payload?.exp) return;
      if (payload.exp * 1000 - Date.now() <= 60 * 1000) {
        refreshAndLoadUser();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!token) return;

    const payload = decodeJwt(token);
    if (!payload?.exp) return;

    const expiryTime = payload.exp * 1000;
    const now = Date.now();
    const timeLeft = expiryTime - now;
    const refreshThreshold = 60 * 1000; // 1 minute before expiry

    if (timeLeft <= refreshThreshold) {
      refreshAndLoadUser();
      return;
    }

    const timer = setTimeout(() => {
      refreshAndLoadUser();
    }, timeLeft - refreshThreshold);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value: AuthContextType = {
    token,
    user,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
